import { StudentResources } from './resources.js';
/**
 * TIP Shell — Student "Today" home (trusted chrome; parent DOM only).
 *
 * The student's landing surface: your classes, the work deployed to them, and
 * one Open per row that launches the app SEALED (?app=<slug>, a top-level nav
 * into the existing mount path — the Gate still computes and enforces the
 * tier; this page grants nothing).
 *
 * PURE presentation over GET /student/home, which returns SELF data only (my
 * classes, my score). No other student exists in this page's inputs.
 */
import { useEffect, useState } from 'preact/hooks';
import { SealMark, AccountControls, SiteFooter } from './ui.js';
import type { ServerClient, StudentHomeResult } from './api.js';

export function StudentHome({ api }: { api: ServerClient }) {
  const [home, setHome] = useState<StudentHomeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { document.title = 'Today · TIP'; }, []);
  useEffect(() => {
    api.studentHome().then(setHome).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [api]);

  const launch = (slug: string, assignmentId: string) => {
    const u = new URL(location.href);
    u.searchParams.set('app', slug);
    u.searchParams.set('assignment', assignmentId); // the Gate binds the mount to it
    location.assign(u.toString());
  };

  return (
    <div>
      <header class="top">
        <SealMark />
        <span class="wordmark">TIP</span>
        <span class="crumb">Today</span>
        <span class="spacer" />
        <AccountControls api={api} role="student" />
      </header>
      <hr class="hairline top-rule" />

      <main class="sheet-field" style="max-width:820px;">
        <StudentResources api={api}/>
        {error ? <p class="notice-line">Today could not load: {error}</p> : null}
        {home === null ? (
          <p style="color:var(--ink-faint);">Loading your work…</p>
        ) : home.deny ? (
          <p class="notice-line">⛔ {home.reason}</p>
        ) : home.classes.length === 0 ? (
          <div>
            <h2 style="font:700 30px/1.15 var(--sans);letter-spacing:-0.02em;margin:0 0 10px;">
              Nothing here yet
            </h2>
            <p style="color:var(--ink-soft);max-width:52ch;">
              When your teacher assigns something, it appears here — already sealed,
              already governed.
            </p>
          </div>
        ) : (
          home.classes.map((c, i) => (
            <section key={c.classId} class={i === 0 ? '' : 'section-gap'}>
              <div class="class-head">
                <h2>{c.name}</h2>
              </div>
              {c.assignments.length === 0 ? (
                <div class="ledger-line" style="color:var(--ink-faint);">Nothing assigned yet.</div>
              ) : (
                c.assignments.map((a) => (
                  <div class="asg-row" key={a.assignmentId}>
                    <div class="row-top">
                      <span class="title">{a.title}</span>
                      <span class="sub">{a.appName} · out of {a.maxScore}</span>
                    </div>
                    <div class="row-act">
                      {a.myScore !== null ? (
                        <span>
                          <span class="dot ok" />
                          <span class="state-ok">Done</span>
                          <span style="color:var(--ink-soft);"> — you scored {a.myScore}/{a.maxScore}</span>
                        </span>
                      ) : (
                        <span class="ready">Not started</span>
                      )}
                      <span class="push">
                        <button
                          type="button"
                          class={a.myScore === null ? 'act' : 'quiet'}
                          style={a.myScore === null ? 'padding:10px 20px;' : ''}
                          onClick={() => launch(a.appSlug, a.assignmentId)}
                        >
                          {a.myScore === null ? 'Open' : 'Play again'}
                        </button>
                      </span>
                    </div>
                  </div>
                ))
              )}
            </section>
          ))
        )}

        <p class="record-voice i" style="color:var(--ink-faint);font-size:15px;margin-top:56px;">
          Nothing you open here can see who you are. Every launch is on the record.
        </p>
      </main>
      <SiteFooter api={api} />
    </div>
  );
}
